import React, { useEffect, useState } from 'react';
import { logger } from '../utils/logger';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../utils/supabase';
import { motion, AnimatePresence } from 'framer-motion';
import { UserCircle, Phone, MapPin, Heart, LogOut, CheckCircle, Loader2, User, Mail, Calendar, Users } from 'lucide-react';
interface FloatingLabelInputProps {
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  type?: string;
  required?: boolean;
  icon?: React.ReactNode;
}
const FloatingLabelInput: React.FC<FloatingLabelInputProps> = ({
  label,
  value,
  onChange,
  type = 'text',
  required = false,
  icon
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const hasValue = value && value.length > 0;
  // Date inputs always show browser placeholder text, so label must always float
  const isDateType = type === 'date';
  const shouldFloat = isFocused || hasValue || isDateType;
  return <div className="relative" data-id="element-6635" data-mp="3el4zxwl1zu2k">
      <input type={type} value={value} onChange={onChange} onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)} required={required} className={`w-full px-4 py-3.5 pt-6 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:ring-4 focus:ring-teal-100 transition-all duration-200 outline-none ${icon ? 'pr-12' : ''} ${isDateType ? 'cursor-pointer' : ''}`} placeholder=" " data-id="element-6636" data-mp="3ekdbd5pviwls" />
      

      <label className={`absolute left-4 transition-all duration-200 pointer-events-none ${shouldFloat ? 'top-2 text-xs font-medium text-teal-600' : 'top-1/2 -translate-y-1/2 text-gray-500'}`} data-id="element-6637" data-mp="157d2kjj3490">
        
        {label}
        {required && <span className="text-rose-500 ml-0.5" data-id="element-6638" data-mp="b3n1gfr67zet">*</span>}
      </label>
      {icon && <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-6639" data-mp="66mum2mhysmn">
          {icon}
        </div>}
    </div>;
};
const PatientSelfRegisterPage: React.FC = () => {
  const {
    user,
    logout
  } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  const [existingPatient, setExistingPatient] = useState<any>(null);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: user?.email || '',
    date_of_birth: '',
    gender: '',
    identification_number: '',
    phone: '',
    address: '',
    city: '',
    state: ''
  });
  const [emergencyContact, setEmergencyContact] = useState({
    name: '',
    relationship: '',
    phone: ''
  });
  useEffect(() => {
    const checkExistingPatient = async () => {
      if (!user?.email) {
        setLoading(false);
        return;
      }
      try {
        const {
          data,
          error: queryError
        } = await supabase.from('patients').select('*').eq('email', user.email).maybeSingle();
        if (queryError) {
          logger.error('Error checking existing patient:', queryError);
          setLoading(false);
          return;
        }
        if (data) {
          setAlreadyRegistered(true);
          setExistingPatient(data);
        }
      } catch (err) {
        logger.error('Error in checkExistingPatient:', err);
      } finally {
        setLoading(false);
      }
    };
    checkExistingPatient();
  }, [user?.email]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      if (!formData.first_name || !formData.last_name || !formData.email || !formData.date_of_birth || !formData.gender || !formData.phone) {
        setError('Por favor complete todos los campos obligatorios');
        setSubmitting(false);
        return;
      }
      const {
        data: insertedPatient,
        error: insertError
      } = await supabase.from('patients').insert([{
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        date_of_birth: formData.date_of_birth,
        gender: formData.gender.toLowerCase(),
        identification_number: formData.identification_number || null,
        phone: formData.phone,
        address: formData.address || null,
        city: formData.city || null,
        state: formData.state || null,
        emergency_contact: emergencyContact,
        created_at: new Date().toISOString()
      }]).select().single();
      if (insertError) {
        logger.error('Supabase insert error:', insertError);
        setError(`Error al registrar: ${insertError.message}`);
        setSubmitting(false);
        return;
      }
      try {
        await supabase.from('audit_logs').insert([{
          user_id: user?.id || 'paciente-system',
          user_name: user?.name || 'Sistema de Registro de Pacientes',
          action: 'create',
          module: 'patients',
          entity_id: insertedPatient.id,
          entity_name: `${formData.first_name} ${formData.last_name}`,
          details: `Paciente registrado mediante auto-registro: ${formData.first_name} ${formData.last_name} (${formData.phone})`,
          created_at: new Date().toISOString()
        }]);
      } catch (auditError) {
        logger.error('Error creating audit log:', auditError);
      }
      setSuccess(true);
    } catch (err) {
      logger.error('Registration error:', err);
      setError(err instanceof Error ? err.message : 'Error desconocido al registrar paciente');
    } finally {
      setSubmitting(false);
    }
  };
  if (loading) {
    return <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50 flex items-center justify-center" data-id="element-6640" data-mp="vvmrcxzqg0og">
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="text-center" data-id="element-6641" data-mp="qu77vad7e286">
          
          <Loader2 className="w-12 h-12 text-teal-600 animate-spin mx-auto mb-4" data-id="element-6642" data-mp="229m60hh5f04v" />
          <p className="text-gray-600 font-medium" data-id="element-6643" data-mp="29edlb2i3hbe9">Cargando...</p>
        </motion.div>
      </div>;
  }
  if (alreadyRegistered && existingPatient) {
    return <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50 flex items-center justify-center p-4" data-id="element-6644" data-mp="2ef1jq98nkuii">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full border border-teal-100" data-id="element-6645" data-mp="2oausrin5y5vj">
          
          <div className="text-center mb-6" data-id="element-6646" data-mp="1vlnrwd08l474">
            <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-6647" data-mp="1vkcssqhqkkmc">
              <CheckCircle className="w-10 h-10 text-white" data-id="element-6648" data-mp="25cii1sglsnxu" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-6649" data-mp="17x4mq6chh7j9">
              Ya Estás Registrado
            </h2>
            <p className="text-gray-600" data-id="element-6650" data-mp="1cvfzaou5xw56">
              Tu información ya está en nuestro sistema
            </p>
          </div>

          <div className="bg-teal-50 rounded-xl p-6 mb-6 space-y-3" data-id="element-6651" data-mp="3q5pf3n6btx0z">
            <div className="flex items-center gap-3" data-id="element-6652" data-mp="3m4dy261iqk4e">
              <User className="w-5 h-5 text-teal-600" data-id="element-6653" data-mp="3m2ja0iuyabw3" />
              <div data-id="element-6654" data-mp="3h4hv8a4k4yqf">
                <p className="text-sm text-gray-600" data-id="element-6655" data-mp="3h3zp8ls3f65b">Nombre</p>
                <p className="font-semibold text-gray-900" data-id="element-6656" data-mp="3c799jzk7ackf">
                  {existingPatient.first_name || existingPatient.name}{' '}
                  {existingPatient.last_name || ''}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3" data-id="element-6657" data-mp="32fdbupm10ogq">
              <Phone className="w-5 h-5 text-teal-600" data-id="element-6658" data-mp="2ye9vm202gqez" />
              <div data-id="element-6659" data-mp="2ycyobs7ex0j4">
                <p className="text-sm text-gray-600" data-id="element-6660" data-mp="2tg8cjh2n4r3i">Teléfono</p>
                <p className="font-semibold text-gray-900" data-id="element-6661" data-mp="2tht6p5l1m80u">
                  {existingPatient.phone}
                </p>
              </div>
            </div>
          </div>

          <button onClick={logout} className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-3 rounded-xl font-semibold hover:from-teal-700 hover:to-cyan-700 transition-all duration-200 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl" data-id="element-6662" data-mp="2jj3otct5cnkl">
            
            <LogOut className="w-5 h-5" data-id="element-6663" data-mp="297em1i0idmu" />
            Cerrar Sesión
          </button>
        </motion.div>
      </div>;
  }
  if (success) {
    return <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50 flex items-center justify-center p-4" data-id="element-6664" data-mp="79lqjumqi7q5">
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.5
      }} className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full border border-teal-100" data-id="element-6665" data-mp="3tegydbfexvo0">
          
          <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          delay: 0.2,
          type: 'spring',
          stiffness: 200
        }} className="text-center mb-6" data-id="element-6666" data-mp="3kk17xipittd1">
            
            <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-6667" data-mp="2r1o0phqqsay0">
              <CheckCircle className="w-10 h-10 text-white" data-id="element-6668" data-mp="2n2xck04983n1" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-6669" data-mp="3126ouopcadlq">
              ¡Registro Exitoso!
            </h2>
            <p className="text-gray-600" data-id="element-6670" data-mp="2w3lqkutne3zx">
              Tu información ha sido guardada correctamente
            </p>
          </motion.div>

          <button onClick={logout} className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-3 rounded-xl font-semibold hover:from-teal-700 hover:to-cyan-700 transition-all duration-200 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl" data-id="element-6671" data-mp="36089he2eykzb">
            
            <LogOut className="w-5 h-5" data-id="element-6672" data-mp="3tp17dg2lj2wk" />
            Cerrar Sesión
          </button>
        </motion.div>
      </div>;
  }
  return <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50" data-id="element-6673" data-mp="3oqzgi8yxhzaj">
      <motion.div initial={{
      y: -20,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-8 px-4 shadow-lg" data-id="element-6674" data-mp="njw152cg4p3u">
        
        <div className="max-w-4xl mx-auto" data-id="element-6675" data-mp="dsrhvqfqmi37">
          <div className="flex items-center gap-4 mb-2" data-id="element-6676" data-mp="drgie2tbrwhw">
            <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center" data-id="element-6677" data-mp="9sz6z72vfzuo">
              <UserCircle className="w-8 h-8" data-id="element-6678" data-mp="16hoqf1u5d0ir" />
            </div>
            <div data-id="element-6679" data-mp="11ilymj36gqsq">
              <h1 className="text-3xl font-bold" data-id="element-6680" data-mp="11k6s05dra3p0">Registro de Paciente</h1>
              <p className="text-teal-100 mt-1" data-id="element-6681" data-mp="xh0np3a7eml6">
                Complete su información personal
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      <div className="max-w-4xl mx-auto px-4 py-8" data-id="element-6682" data-mp="3i7rctik3mquj">
        <motion.form initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} onSubmit={handleSubmit} className="space-y-6" data-id="element-6683" data-mp="3i8ix3x88x25i">
          
          <AnimatePresence data-id="element-6684" data-mp="1soj4gtf6f6hu">
            {error && <motion.div initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-xl flex items-center gap-3" data-id="element-6685" data-mp="1xp74g4lduxo7">
              
                <div className="w-2 h-2 bg-red-500 rounded-full" data-id="element-6686" data-mp="2bglq6j6odcyy"></div>
                {error}
              </motion.div>}
          </AnimatePresence>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden" data-id="element-6687" data-mp="2lfknx6pwjo76">
            
            <div className="bg-gradient-to-r from-teal-500 to-cyan-500 px-6 py-4 flex items-center gap-3" data-id="element-6688" data-mp="v2vi71cfq4k1">
              <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center" data-id="element-6689" data-mp="1ydj2papdpv91">
                <User className="w-5 h-5 text-white" data-id="element-6690" data-mp="1tf7vl7qgxyui" />
              </div>
              <div data-id="element-6691" data-mp="27eh7hv7n66so">
                <h2 className="text-xl font-bold text-white" data-id="element-6692" data-mp="27ez2koikiez0">
                  Información Personal
                </h2>
                <p className="text-teal-50 text-sm" data-id="element-6693" data-mp="23bt1dvabbdze">
                  Datos básicos del paciente
                </p>
              </div>
            </div>

            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-6694" data-mp="2cdk18ji68qlc">
              <FloatingLabelInput label="Nombre" value={formData.first_name} onChange={e => setFormData({
              ...formData,
              first_name: e.target.value
            })} required icon={<User className="w-5 h-5" data-id="element-6696" data-mp="2m3vgh27b2m7j" />} data-id="element-6695" data-mp="2cdaed4sfazjv" />
              

              <FloatingLabelInput label="Apellido" value={formData.last_name} onChange={e => setFormData({
              ...formData,
              last_name: e.target.value
            })} required icon={<User className="w-5 h-5" data-id="element-6698" data-mp="3epbswn4oof0h" />} data-id="element-6697" data-mp="302d3expdzuoe" />
              

              <FloatingLabelInput label="Correo Electrónico" type="email" value={formData.email} onChange={e => setFormData({
              ...formData,
              email: e.target.value
            })} required icon={<Mail className="w-5 h-5" data-id="element-6700" data-mp="35rhwlc6x5ldb" />} data-id="element-6699" data-mp="3enqugmf90bxc" />
              

              <FloatingLabelInput label="Fecha de Nacimiento" type="date" value={formData.date_of_birth} onChange={e => setFormData({
              ...formData,
              date_of_birth: e.target.value
            })} required icon={<Calendar className="w-5 h-5" data-id="element-6702" data-mp="30rdg3m60gqn3" />} data-id="element-6701" data-mp="39pflsf0ow7gp" />
              

              <div className="relative" data-id="element-6703" data-mp="2m27ghx39gyeo">
                <select value={formData.gender} onChange={e => setFormData({
                ...formData,
                gender: e.target.value
              })} required className="w-full px-4 py-3.5 pt-6 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:ring-4 focus:ring-teal-100 transition-all duration-200 appearance-none bg-white outline-none" data-id="element-6704" data-mp="2lz38spql7kj9">
                  
                  <option value="" data-id="element-6705" data-mp="29y3h2z2tvu6e">Seleccionar...</option>
                  <option value="masculino" data-id="element-6706" data-mp="24zig2xfr56gw">Masculino</option>
                  <option value="femenino" data-id="element-6707" data-mp="2524qd4u1202t">Femenino</option>
                  <option value="otro" data-id="element-6708" data-mp="207qyvdihkd3f">Otro</option>
                </select>
                <label className="absolute left-4 top-2 text-xs font-medium text-teal-600 transition-all duration-200 pointer-events-none" data-id="element-6709" data-mp="1w89smhv8jbx7">
                  Género<span className="text-rose-500 ml-0.5" data-id="element-6710" data-mp="1r6aoh5ro8az7">*</span>
                </label>
                <Users className="w-5 h-5 text-gray-400 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-6711" data-mp="1m7zf0w5ag2hi" />
              </div>

              <FloatingLabelInput label="Número de Identificación" value={formData.identification_number} onChange={e => setFormData({
              ...formData,
              identification_number: e.target.value
            })} icon={<UserCircle className="w-5 h-5" data-id="element-6713" data-mp="37mdnrrdk763u" />} data-id="element-6712" data-mp="1d8vudnqh3f0w" />
              
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden" data-id="element-6714" data-mp="tdt8nrv2wlrl">
            
            <div className="bg-gradient-to-r from-cyan-500 to-teal-500 px-6 py-4 flex items-center gap-3" data-id="element-6715" data-mp="176soxw2oqk07">
              <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center" data-id="element-6716" data-mp="ags2i3psgfm2">
                <Phone className="w-5 h-5 text-white" data-id="element-6717" data-mp="ffwa4px2e5aa" />
              </div>
              <div data-id="element-6718" data-mp="jgf3ioelvjel">
                <h2 className="text-xl font-bold text-white" data-id="element-6719" data-mp="jfwz34hshyvl">
                  Información de Contacto
                </h2>
                <p className="text-cyan-50 text-sm" data-id="element-6720" data-mp="oh2pgvnz9a1y">
                  Datos de ubicación y contacto
                </p>
              </div>
            </div>

            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-6721" data-mp="1zu58ryhgodpt">
              <FloatingLabelInput label="Teléfono" type="tel" value={formData.phone} onChange={e => setFormData({
              ...formData,
              phone: e.target.value
            })} required icon={<Phone className="w-5 h-5" data-id="element-6723" data-mp="9quvcv20ogdi" />} data-id="element-6722" data-mp="3vxt6fxkuzty1" />
              

              <FloatingLabelInput label="Dirección" value={formData.address} onChange={e => setFormData({
              ...formData,
              address: e.target.value
            })} icon={<MapPin className="w-5 h-5" data-id="element-6725" data-mp="3d8mssoyx9sj3" />} data-id="element-6724" data-mp="9qcutm8bnfzs" />
              

              <FloatingLabelInput label="Ciudad" value={formData.city} onChange={e => setFormData({
              ...formData,
              city: e.target.value
            })} icon={<MapPin className="w-5 h-5" data-id="element-6727" data-mp="3mzhhs13jz688" />} data-id="element-6726" data-mp="388s3uqypjd0o" />
              

              <FloatingLabelInput label="Estado/Provincia" value={formData.state} onChange={e => setFormData({
              ...formData,
              state: e.target.value
            })} icon={<MapPin className="w-5 h-5" data-id="element-6729" data-mp="2pisk8jlmyq4n" />} data-id="element-6728" data-mp="3i5mz90zooxaq" />
              
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden" data-id="element-6730" data-mp="14zq3jamzgsa1">
            
            <div className="bg-gradient-to-r from-rose-500 to-pink-500 px-6 py-4 flex items-center gap-3" data-id="element-6731" data-mp="100luck026ujp">
              <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center" data-id="element-6732" data-mp="1npy0fb03amhs">
                <Heart className="w-5 h-5 text-white" data-id="element-6733" data-mp="1sohs7s1d6si2" />
              </div>
              <div data-id="element-6734" data-mp="1eqb0yuqx7qrd">
                <h2 className="text-xl font-bold text-white" data-id="element-6735" data-mp="1eoq6t68iq9u1">
                  Contacto de Emergencia
                </h2>
                <p className="text-rose-50 text-sm" data-id="element-6736" data-mp="1jnba5ej2h9mp">
                  Persona a contactar en caso de emergencia
                </p>
              </div>
            </div>

            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-6737" data-mp="89z8naas533p">
              <FloatingLabelInput label="Nombre del Contacto" value={emergencyContact.name} onChange={e => setEmergencyContact({
              ...emergencyContact,
              name: e.target.value
            })} icon={<User className="w-5 h-5" data-id="element-6739" data-mp="pykw92pew27t" />} data-id="element-6738" data-mp="yzj3ruz26rv5" />
              

              <FloatingLabelInput label="Relación" value={emergencyContact.relationship} onChange={e => setEmergencyContact({
              ...emergencyContact,
              relationship: e.target.value
            })} icon={<Users className="w-5 h-5" data-id="element-6741" data-mp="1ipl11fvkha0j" />} data-id="element-6740" data-mp="q173f18e4fpi" />
              

              <FloatingLabelInput label="Teléfono de Emergencia" type="tel" value={emergencyContact.phone} onChange={e => setEmergencyContact({
              ...emergencyContact,
              phone: e.target.value
            })} icon={<Phone className="w-5 h-5" data-id="element-6743" data-mp="13wrnhzzuv3yi" />} data-id="element-6742" data-mp="1dq05lfflvjsk" />
              
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.5
        }} className="flex gap-4" data-id="element-6744" data-mp="21gckxbd6kit6">
            
            <button type="submit" disabled={submitting} className="flex-1 bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-4 rounded-xl font-semibold hover:from-teal-700 hover:to-cyan-700 transition-all duration-200 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-6745" data-mp="28xitrqmwr5z9">
              
              {submitting ? <>
                  <Loader2 className="w-5 h-5 animate-spin" data-id="element-6746" data-mp="2rwn18uyswrot" />
                  Registrando...
                </> : <>
                  <CheckCircle className="w-5 h-5" data-id="element-6747" data-mp="2wmtn4n1xhy3d" />
                  Registrar Paciente
                </>}
            </button>

            <button type="button" onClick={logout} className="px-6 py-4 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-all duration-200 flex items-center gap-2" data-id="element-6748" data-mp="31l4wkwoba6l2">
              
              <LogOut className="w-5 h-5" data-id="element-6749" data-mp="3ane4rfw3xnva" />
              Salir
            </button>
          </motion.div>
        </motion.form>
      </div>
    </div>;
};
export { PatientSelfRegisterPage };
export default PatientSelfRegisterPage;